import React, { useMemo, useState } from 'react';
import { Form } from 'semantic-ui-react';
import { Reservations } from '../../api/services/next';
import { DatePicker } from '../../components/shared/DatePicker';
import HotelsDropDown from '../../components/shared/HotelsDropDown';
import TextInput from '../../components/shared/TextInput';
import { useTextResources } from '../../language';
import GridSearchTemplate from '../GridSearchTemplate';
import moment from 'moment';
import { endpoint } from '../../api/http';
import CustomerDropDown from '../../components/shared/CustomerDropDown';
import ExternalChannelDropDown from '../../components/shared/ExternalChannelDropDown';
import ReservationStatusesDropDown from '../../components/shared/ReservationStatusesDropDown';
import GuestSearchDropDown from '../../components/shared/GuestSearchDropDown';
import RatesDropDown from '../../components/shared/RatesDropDown';
import { Checkbox } from 'semantic-ui-react';
import ReservationTypesDropDown from '../../components/shared/ReservationTypesDropDown';
import RoomTypesDropDown from '../../components/shared/RoomTypesDropDown';
import PropTypes from 'prop-types';
import { dateFormat } from '../../components/shared/constants';

const ReservationSearch = ({isUnassignedRoomsOnly, statuses }) => {
  const text = useTextResources();
  const [unassignedRoomsOnly, setUnassignedRoomsOnly] = useState(
    isUnassignedRoomsOnly
  );
  const [hotelIds, setHotelIds] = useState([]);
  const [guest, setGuest] = useState({});
  
  const dateFormatter = ({ value }) => {
    return value && moment(value).format(dateFormat);
  };

  const RoomTypesSpec = React.useMemo(() => {
    let spec = {
      HotelIds: hotelIds,
    };
    return spec;
  }, [hotelIds]);

  /* eslint-disable react/prop-types */
  const reservationFormatter = ({ value, row }) => {
    if(row) {
      if (row.ReservationGroupId && !row.IsPartyGroup) {
        return (
          <a
            target='_blank'
            rel='noopener noreferrer'
            href={`${endpoint}PMS/ReservationGroupDataService/ReservationGroup?groupid=${row.ReservationGroupId}&hotelid=${row.HotelId}`}
          >
            {value}
          </a>
        );
      } else {
        return (
          <a
            target='_blank'
            rel='noopener noreferrer'
            href={`${endpoint}PMS/Reservations/Index/${row.Id}?hotelid=${row.HotelId}`}
          >
            {value}
          </a>
        );
      }
    }
    else return value || "";
  };
  const guestFormatter = ({ value, row }) => {
      return row ? (
          <a
              target='_blank'
              rel='noopener noreferrer'
              href={`${endpoint}CRM/GuestProfile/index/?hotelid=${row.HotelId}#/profile/${row.GuestId}`}
          >
              {value}
          </a>
      ) : value || ""; 
  };
  /* eslint-enable react/prop-types */
  const CustomersSpec = React.useMemo(() => {
    return {
      GetOnlyCustomerArray: true,
      IsActive: true,
      IncludeCustomerTypeId: true,
    };
  }, []);

  const RatesSpec = React.useMemo(() => {
    return { hotelIds };
  }, [hotelIds]);

  const onChangeGuestvalue = (value) => {
    if (!value?.toString().startsWith('newguest-')) {
      setGuest({ GuestId: value });
    }
  };

  const datePredicate = (value, filter) => {
    return dateFormatter({value})?.includes(filter?.value?.toLowerCase())
  }

  const gridOptions = {
    search: {
      fields: [
        <Form.Field
          key='HotelIds'
          onChange={setHotelIds}
          value={hotelIds}
          multiple
          label={text('Hotel')}
          control={HotelsDropDown}
          spec={useMemo(
            () => ({
              IsActive: true,
              IncludeOnlyHotelsWithUserPermissions: true,
            }),
            []
          )}
          placeholder={text('All')}
          clearable
        />,
        <Form.Field
          key='Number'
          label={text('ReservationNumber')}
          control={TextInput}
          buttonPlacement='none'
          digits={0}
        />,
        <DatePicker
          type='range'
          label={text('ArrivalDate')}
          key='CheckInDateRange'
        />,
        <DatePicker
          type='range'
          label={text('DepartureDate')}
          key='CheckOutDateRange'
        />,
        <Form.Field
          key='Statuses'
          label={text('ReservationStatus')}
          placeholder={text('All')}
          multiple
          clearable
          control={ReservationStatusesDropDown}
        />,
        <Form.Field
          key='GuestId'
          label={text('Guest')}
          width={3}
          clearable
          control={GuestSearchDropDown}
          placeholder={text('SearchByNameEmailPhoneId')}
          additionLabel={`${text('Search')}: `}
          onAddGuest={setGuest}
          onChange={onChangeGuestvalue}
        />,
      ],
      advancedFields: [
        <Form.Field
          key='RateId'
          label={text('Rate')}
          width={3}
          placeholder={text('All')}
          clearable
          control={RatesDropDown}
          spec={RatesSpec}
        />,
        <DatePicker
          type='range'
          label={text('CreateDate')}
          key='CreateDateRange'
        />,
        <Form.Field
          key='CustomerID'
          label={text('Customer')}
          placeholder={text('All')}
          width={3}
          clearable
          control={CustomerDropDown}
          spec={CustomersSpec}
        />,
        <Form.Field
          key='ExternalChannelID'
          label={text('ExternalChannel')}
          placeholder={text('All')}
          clearable
          control={ExternalChannelDropDown}
        />,
        <Form.Field
          key='ExternalReservationMasterId'
          label={text('ExternalReservationMasterID')}
          control={TextInput}
        />,
        <Form.Field
          key='RoomTypeId'
          label={text('RoomType')}
          placeholder={text('All')}
          HotelIDs={hotelIds}
          control={RoomTypesDropDown}
          clearable
          spec={RoomTypesSpec}
        />,
        <Form.Field
          key='ReservationTypes'
          control={ReservationTypesDropDown}
          placeholder={text('All')}
          clearable
          multiple
          label={text('ReservationType')}
        />,
        <Form.Field key='UnassignedRoomsOnly'>
          <label>{text('ReservationsWithoutAllocatedRoom')}</label>
          <Checkbox
            onChange={(e, { checked }) => setUnassignedRoomsOnly(checked)}
            checked={unassignedRoomsOnly}
          />
        </Form.Field>,
      ],
      method: 'POST',
      api: Reservations.Search,
      searchOnLoad: isUnassignedRoomsOnly,
      spec: {
        MaxResults: 1000,
        UnassignedRoomsOnly: unassignedRoomsOnly,
        FreeText:
          (guest?.FirstName || guest?.LastName) &&
          `${guest?.FirstName} ${guest?.LastName}`,
        GuestId: guest?.GuestId?.toString().startsWith('newguest-')
          ? null
          : guest?.GuestId,
      },
      initial: unassignedRoomsOnly
        ? {
            HotelIds: hotelIds,
            ExcludeBlockReservations: true,
            Statuses: statuses,
            IsUnassignedRoomsOnly: unassignedRoomsOnly
          }
        : null,
    },
    grid: {
        columns: [
        {
          name: 'Number',
          title: text('Number'),
          formatter: reservationFormatter,
        },
        { name: 'Hotel', title: text('Hotel') },
        {
          name: 'CheckInDate',
          title: text('ArrivalDate'),
          formatter: dateFormatter,
        },
        {
          name: 'CheckOutDate',
          title: text('DepartureDate'),
          formatter: dateFormatter,
        },
        { name: 'GuestName', title: text('Guest'), formatter: guestFormatter },
        { name: 'GuestPhone', title: text('GuestPhone') },
        { name: 'Company', title: text('Company') },
        { name: 'Agent', title: text('Agent') },
        { name: 'Room', title: text('Room') },
        { name: 'Status', title: text('Status') },
        { name: 'Product', title: text('Product') },
      ],
      paging: true,
      scrollbarX:true,
      cacheId:'ReservationSearch',
        customOptions:{
          hidden: {
            toggleHidden:true
           },
            group: {
              grouping: [{ columnName: 'Hotel' }]
            },
            order:{},
            sort:{
              sorting:[{ columnName: 'Number', direction: 'desc' }]
            }
        },
      filtering: {
        showByDefault:true,
        extensions: [
          {columnName: 'CheckInDate', predicate: datePredicate},
          {columnName: 'CheckOutDate', predicate: datePredicate}
        ]
      },
    },
  };
  return <GridSearchTemplate options={gridOptions} />;
};

ReservationSearch.propTypes = {
  hotelId: PropTypes.any,
  isUnassignedRoomsOnly: PropTypes.bool,
  statuses: PropTypes.array
};
export default ReservationSearch;
